import { apiService } from './apiService';
import { ConversationMessage } from './conversationService';
import { aiProviderManager } from './aiProviderService';

export interface ConversationSummary {
  id?: string;
  user_id: string;
  session_date: string;
  message_count: number;
  topics_discussed: string[];
  user_sentiment: 'positive' | 'negative' | 'neutral' | 'mixed';
  support_types: string[];
  key_moments: string[];
  sobriety_days_at_time?: number;
  created_at?: string;
  updated_at?: string;
}

class ConversationSummaryService {
  private static instance: ConversationSummaryService;

  static getInstance(): ConversationSummaryService {
    if (!ConversationSummaryService.instance) {
      ConversationSummaryService.instance = new ConversationSummaryService();
    }
    return ConversationSummaryService.instance;
  }

  async generateSessionSummary(
    userId: string,
    messages: ConversationMessage[],
    sobrietyDays?: number
  ): Promise<ConversationSummary> {
    const today = new Date().toISOString().split('T')[0];
    
    // Try AI-powered summarization first
    let topics: string[] = [];
    let sentiment: 'positive' | 'negative' | 'neutral' | 'mixed' = 'neutral';
    let keyMoments: string[] = [];

    try {
      console.log('🤖 ConversationSummaryService: Attempting AI-powered summarization...');
      
      // Prepare conversation history for AI
      const conversationText = messages.map(msg => 
        `${msg.role === 'user' ? 'Usuário' : 'Bill'}: ${msg.content}`
      ).join('\n\n');

      const aiSummary = await aiProviderManager.summarizeConversation(conversationText, {
        conversationLength: messages.length,
        tokenLimit: 2000
      });

      if (aiSummary) {
        topics = aiSummary.topics || [];
        sentiment = aiSummary.sentiment as 'positive' | 'negative' | 'neutral' | 'mixed' || 'neutral';
        keyMoments = [aiSummary.summary];
        console.log('✅ ConversationSummaryService: AI summarization successful', {
          topics: topics.length,
          sentiment,
          summaryLength: aiSummary.summary.length
        });
      }
    } catch (error) {
      console.warn('⚠️ ConversationSummaryService: AI summarization failed, using fallback', error);
    }

    // Fallback to rule-based extraction if AI failed
    if (topics.length === 0) {
      topics = this.extractTopics(messages);
      sentiment = this.analyzeSentiment(messages);
      keyMoments = this.extractKeyMoments(messages);
      console.log('📋 ConversationSummaryService: Using fallback rule-based summarization');
    }
    
    const supportTypes = this.extractSupportTypes(messages);

    const summary: ConversationSummary = {
      user_id: userId,
      session_date: today,
      message_count: messages.length,
      topics_discussed: topics,
      user_sentiment: sentiment,
      support_types: supportTypes,
      key_moments: keyMoments,
      sobriety_days_at_time: sobrietyDays
    };

    return summary;
  }

  /**
   * Save conversation summary via API (bypasses RLS timing issues)
   */
  async saveSummary(summary: ConversationSummary): Promise<void> {
    try {
      console.log('💾 ConversationSummaryService: Saving summary via API', {
        sessionDate: summary.session_date,
        messageCount: summary.message_count,
        topics: summary.topics_discussed
      });

      const response = await apiService.post<{
        success: boolean;
        action?: string;
        id?: string;
        error?: string;
      }>('/api/conversations/summary', {
        session_date: summary.session_date,
        message_count: summary.message_count,
        topics_discussed: summary.topics_discussed,
        user_sentiment: summary.user_sentiment,
        support_types: summary.support_types,
        key_moments: summary.key_moments,
        sobriety_days_at_time: summary.sobriety_days_at_time
      });

      if (!response.success || !response.data?.success) {
        console.error('❌ Error saving conversation summary:', response.data?.error || response.error);
        return;
      }

      console.log('✅ Conversation summary saved via API:', {
        action: response.data.action,
        id: response.data.id
      });

    } catch (error) {
      console.error('❌ Unexpected error saving conversation summary:', error);
      if (error instanceof Error) {
        console.error('Error details:', { message: error.message, stack: error.stack });
      }
    }
  }

  private extractTopics(messages: ConversationMessage[]): string[] {
    const topics = new Set<string>();
    const topicKeywords = {
      'sobriedade': ['sobriedade', 'sóbrio', 'dias sóbrio', 'abstinência', 'parar de beber'],
      'ansiedade': ['ansioso', 'preocupado', 'nervoso', 'ansiedade', 'stress'],
      'tristeza': ['triste', 'deprimido', 'melancolia', 'down', 'baixo astral'],
      'vontade': ['vontade de beber', 'tentação', 'crave', 'desejo', 'compulsão'],
      'celebração': ['parabéns', 'conquista', 'vitória', 'orgulho', 'marco'],
      'recaída': ['recaída', 'bebi', 'escorregão', 'voltei a beber', 'deslize'],
      'apoio': ['ajuda', 'apoio', 'conversar', 'desabafo', 'suporte'],
      'família': ['família', 'filhos', 'esposa', 'marido', 'pais', 'relacionamento'],
      'trabalho': ['trabalho', 'emprego', 'chefe', 'carreira', 'profissional'],
      'espiritualidade': ['deus', 'fé', 'oração', 'religião', 'espiritualidade', 'poder superior'],
      'grupos': ['aa', 'grupos', 'reunião', 'meeting', 'padrinho', 'apadrinhamento']
    };

    const userMessages = messages.filter(m => m.role === 'user');
    const allText = userMessages.map(m => m.content.toLowerCase()).join(' ');

    Object.entries(topicKeywords).forEach(([topic, keywords]) => {
      if (keywords.some(keyword => allText.includes(keyword))) {
        topics.add(topic);
      }
    });

    return Array.from(topics).slice(0, 5); // Limit to top 5 topics
  }

  private analyzeSentiment(messages: ConversationMessage[]): 'positive' | 'negative' | 'neutral' | 'mixed' {
    const userMessages = messages.filter(m => m.role === 'user');
    if (userMessages.length === 0) return 'neutral';

    const positiveWords = ['bem', 'feliz', 'animado', 'otimista', 'confiante', 'forte', 'melhor', 'bom', 'ótimo'];
    const negativeWords = ['triste', 'ansioso', 'preocupado', 'difícil', 'ruim', 'problema', 'crise', 'mal'];

    let positiveCount = 0;
    let negativeCount = 0;

    const allText = userMessages.map(m => m.content.toLowerCase()).join(' ');

    positiveWords.forEach(word => {
      if (allText.includes(word)) positiveCount++;
    });

    negativeWords.forEach(word => {
      if (allText.includes(word)) negativeCount++;
    });

    if (positiveCount > negativeCount && positiveCount > 0) return 'positive';
    if (negativeCount > positiveCount && negativeCount > 0) return 'negative';
    if (positiveCount > 0 && negativeCount > 0) return 'mixed';
    return 'neutral';
  }

  private extractSupportTypes(messages: ConversationMessage[]): string[] {
    const supportTypes = new Set<string>();
    
    messages.forEach(message => {
      if (message.role === 'assistant' && message.context?.supportType) {
        supportTypes.add(message.context.supportType);
      }
    });

    return Array.from(supportTypes);
  }

  private extractKeyMoments(messages: ConversationMessage[]): string[] {
    const keyMoments: string[] = [];
    
    // Look for milestone celebrations
    const milestoneMessages = messages.filter(m => 
      m.role === 'assistant' && 
      m.context?.supportType === 'celebration' &&
      m.content.includes('🎉')
    );

    milestoneMessages.forEach(message => {
      const preview = message.content.substring(0, 100) + '...';
      keyMoments.push(`Milestone celebration: ${preview}`);
    });

    // Look for crisis support moments
    const crisisMessages = messages.filter(m => 
      m.role === 'assistant' && 
      m.context?.supportType === 'crisis'
    );

    crisisMessages.forEach(message => {
      const preview = message.content.substring(0, 100) + '...';
      keyMoments.push(`Crisis support: ${preview}`);
    });

    // Look for important user insights
    const userMessages = messages.filter(m => m.role === 'user' && m.content.length > 100);
    if (userMessages.length > 0) {
      const longestMessage = userMessages.reduce((prev, current) => 
        prev.content.length > current.content.length ? prev : current
      );
      const preview = longestMessage.content.substring(0, 100) + '...';
      keyMoments.push(`User reflection: ${preview}`);
    }

    return keyMoments.slice(0, 3); // Limit to 3 key moments
  }

  /**
   * Auto-summarize long conversations via API (bypasses RLS timing issues)
   */
  async summarizeLongConversation(messages: any[]): Promise<void> {
    if (messages.length < 15) {
      console.log('📝 ConversationSummaryService: Conversation too short for auto-summarization', {
        currentCount: messages.length,
        requiredCount: 15
      });
      return;
    }

    try {
      console.log('🔄 ConversationSummaryService: Auto-summarizing long conversation', {
        messageCount: messages.length
      });

      // Extract user ID from messages - try multiple methods
      let userId: string | undefined;

      // Method 1: Check if messages have userId property (added in BillContext)
      const messageWithUserId = messages.find((m: any) => m.userId);
      if (messageWithUserId) {
        userId = messageWithUserId.userId;
        console.log('✅ ConversationSummaryService: Found userId from message property', { userId });
      }

      if (!userId) {
        console.warn('⚠️ ConversationSummaryService: Cannot determine user ID for auto-summarization', {
          messagesSample: messages.slice(0, 2).map((m: any) => ({
            id: m.id,
            role: m.role,
            hasUserId: !!m.userId,
            contentPreview: m.content?.substring(0, 50)
          }))
        });
        return;
      }

      // Check if we already have a recent summary to avoid duplicates via API
      try {
        const todayResponse = await apiService.get<{
          success: boolean;
          exists?: boolean;
          summary?: { id: string; created_at: string };
          error?: string;
        }>('/api/conversations/summary/today');

        if (todayResponse.success && todayResponse.data?.exists && todayResponse.data.summary) {
          const lastSummaryTime = new Date(todayResponse.data.summary.created_at).getTime();
          const now = new Date().getTime();
          const hoursSinceLastSummary = (now - lastSummaryTime) / (1000 * 60 * 60);

          if (hoursSinceLastSummary < 2) {
            console.log('⏭️ ConversationSummaryService: Recent summary exists, skipping', {
              hoursSinceLastSummary: hoursSinceLastSummary.toFixed(1)
            });
            return;
          }
        }
      } catch (checkError) {
        console.warn('⚠️ Could not check for existing summary, continuing with summarization');
      }

      // Convert messages to proper format
      const conversationMessages: ConversationMessage[] = messages.map((msg: any) => ({
        id: msg.id || `msg_${Date.now()}_${Math.random()}`,
        content: msg.content || msg.text || '',
        role: msg.role,
        timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
        context: msg.context
      }));

      // Create a summary of the conversation
      const summary = await this.generateSessionSummary(userId, conversationMessages);
      
      // Save the summary via API
      await this.saveSummary(summary);

      console.log('✅ ConversationSummaryService: Auto-summarization completed', {
        messageCount: messages.length,
        topics: summary.topics_discussed,
        sentiment: summary.user_sentiment,
        userId,
        summaryDate: summary.session_date
      });

    } catch (error) {
      console.error('❌ ConversationSummaryService: Auto-summarization failed:', error);
      if (error instanceof Error) {
        console.error('❌ Error details:', { message: error.message, stack: error.stack });
      }
    }
  }

  /**
   * Get recent conversation summaries via API (bypasses RLS timing issues)
   * Note: userId parameter is kept for backwards compatibility but not used (API uses JWT)
   */
  async getRecentSummaries(_userId: string, limit: number = 7): Promise<ConversationSummary[]> {
    try {
      console.log('📋 ConversationSummaryService: Loading summaries via API...');

      const response = await apiService.get<{
        success: boolean;
        summaries?: any[];
        error?: string;
      }>(`/api/conversations/summaries?limit=${limit}`);

      if (!response.success || !response.data?.success) {
        console.error('Error fetching conversation summaries:', response.data?.error || response.error);
        return [];
      }

      // Transform the data to match ConversationSummary interface
      const summaries = (response.data.summaries || []).map((row: any) => ({
        id: row.id,
        user_id: row.user_id,
        session_date: row.session_date,
        message_count: row.message_count || 0,
        topics_discussed: row.topics_discussed || [],
        user_sentiment: row.user_sentiment || 'neutral',
        support_types: row.support_types || [],
        key_moments: row.bill_response ? [row.bill_response] : [],
        sobriety_days_at_time: row.sobriety_days_at_time,
        created_at: row.created_at,
        updated_at: row.updated_at
      }));

      console.log('✅ ConversationSummaryService: Summaries loaded via API:', summaries.length);
      return summaries;

    } catch (error) {
      console.error('Unexpected error fetching summaries:', error);
      return [];
    }
  }
}

export default ConversationSummaryService.getInstance();
